import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Box, ModalTrigger, Paragraph, Popover, Text, TextButton } from '@zillow/constellation-10';

import accessUtils from '../../../../utils/accessUtils';
import typeCheckUtils from '../../../../utils/typeCheckUtils';
import propertyUtils from '../../../../utils/propertyUtils';

import CloneThisListingConversionModal from '../../../Modal/CloneThisListingConversionModal';
import RecommendationsLearnMoreContent from '../../../RecommendationsLearnMoreContent';
import RecommendationIconBulb from '../../../RecommendationIconBulb';
import { MODAL_COMPONENT } from '../constants';
import { RECS, REVIEW_PAGE_SECTION_RECS } from '../../../../constants/RecommendationsConstants';

import AccordionIconError from './AccordionIconError';
import ContentSectionEditButton from './ContentSectionEditButton';
import FieldValueDisplay from './FieldValueDisplay';

import { reviewSectionStyles, StyledIconPlaceholder, StyledReviewSectionTitle, StyledReviewTitleRow } from '../styles';

const { PROPERTY_INFORMATION } = REVIEW_PAGE_SECTION_RECS;

const ariaLabels = {
  PROPERTY_ADDRESS: 'property-address',
  HIDE_PROPERTY_ADDRESS: 'hide-property-address',
  SQUARE_FOOTAGE: 'squre-footage',
  ROOM_SQUARE_FOOTAGE: 'room-square-footage',
  BEDROOMS: 'bedrooms',
  BATHROOMS: 'bathrooms',
  PROPERTY_TYPE: 'property-type',
};

const propTypes = {
  onEditClick: PropTypes.func.isRequired,
};
const defaultProps = {};

const PropertyInformationContent = ({ onEditClick }) => {
  const {
    user: {
      features: { canUpdateListing },
    },
    properties: {
      recommendations,
      currentProperty,
      currentProperty: {
        isArchived,
        isFeed,
        hideAddress,
        floorplans,
        propertyType,
        description,
        restrictions: { isUpdateListingRestricted, isUpdateUnitNumberRestricted },
      },
    },
  } = useSelector((state) => state);

  const [displayRecsModal, setDisplayRecsModal] = useState(false);

  const customNoneFieldValueText = isFeed ? 'None' : 'Action needed';

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const propertyDescriptionHeading = isRoomForRent ? 'Room and shared space description' : 'Property description';
  const ariaLabelForPropertyDescription = useMemo(
    () => propertyDescriptionHeading.toLowerCase().split().join('-'),
    [propertyDescriptionHeading],
  );

  const floorplan = floorplans[0];
  const isDisabled = isFeed || !canUpdateListing || isArchived || isUpdateListingRestricted;

  const canAccessCloneListing = accessUtils.canAccessCloneListing({ currentProperty });

  const squareFootageRec = recommendations[PROPERTY_INFORMATION][RECS.SQUARE_FOOTAGE];
  const hideStreetAddressRec = recommendations[PROPERTY_INFORMATION][RECS.HIDE_STREET_ADDRESS];
  const descriptionRec = recommendations[PROPERTY_INFORMATION][RECS.DESCRIPTION];

  const { formattedCompleteAddress } = propertyUtils.formatAddress(currentProperty);

  const getSquareFootageDisplay = () => {
    if (floorplan?.squareFeet < 1 || !floorplan?.squareFeet) {
      return null;
    }
    return `${typeCheckUtils.Number(floorplan?.squareFeet)} sq. ft.`;
  };

  const getRoomSquareFootageDisplay = () => {
    if (floorplan?.roomForRent?.roomSquareFeet < 1 || !floorplan?.roomForRent?.roomSquareFeet) {
      return null;
    }
    return `${typeCheckUtils.Number(floorplan?.roomForRent?.roomSquareFeet)} sq. ft.`;
  };

  return (
    <div data-testid="PropertyInformationContent" data-group-title="PropertyInformation">
      <Box css={reviewSectionStyles} data-testid="Address">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder />
            <Text textStyle="body-bold">Address</Text>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.PROPERTY_ADDRESS}
            useLockIcon={isUpdateUnitNumberRestricted}
            onEditClick={onEditClick}
            disabledMessage={
              canAccessCloneListing ? (
                'Once a property is created, its unique property address can’t be edited. Instead, create a new property with the correct address.'
              ) : (
                <Box
                  data-testid="CloneThisListing-Message-Container"
                  css={{ display: 'flex', flexDirection: 'column', gap: '400' }}
                >
                  <Paragraph>
                    Once a property is created, its unique property address can&apos;t be edited. Instead, create a new
                    property with the correct address.
                  </Paragraph>

                  <Paragraph>
                    If you would like to post another unit with similar listing details, you can make a copy.
                  </Paragraph>

                  <Box>
                    <Popover.Close>
                      <ModalTrigger multiModalTriggerId="CloneThisListingConversionModal">
                        <TextButton>Clone this listing</TextButton>
                      </ModalTrigger>
                    </Popover.Close>
                  </Box>
                </Box>
              )
            }
            popoverHeading="Need to edit your property address?"
            value={MODAL_COMPONENT.AddressFormField.componentKey}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={formattedCompleteAddress} />
      </Box>

      <Box css={reviewSectionStyles} data-testid="HidePropertyAddress">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {hideStreetAddressRec && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.HIDE_PROPERTY_ADDRESS}
                  removeFlex={true}
                  onClick={() => {
                    setDisplayRecsModal(RECS.HIDE_STREET_ADDRESS);
                  }}
                />
              )}
            </StyledIconPlaceholder>
            <StyledReviewSectionTitle>
              <Text textStyle="body-bold">Hide property address</Text>
              {!isFeed && (
                <TextButton
                  aria-label={`learn-more-about-${ariaLabels.HIDE_PROPERTY_ADDRESS}`}
                  textStyle="body"
                  onClick={() => {
                    setDisplayRecsModal(RECS.HIDE_STREET_ADDRESS);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </StyledReviewSectionTitle>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.HIDE_PROPERTY_ADDRESS}
            value={MODAL_COMPONENT.HideAddressFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={hideAddress ? 'Yes' : 'No'} />
      </Box>

      <Box css={reviewSectionStyles} data-testid="SquareFootage">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {squareFootageRec && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.SQUARE_FOOTAGE}
                  removeFlex={true}
                  onClick={() => {
                    setDisplayRecsModal(RECS.SQUARE_FOOTAGE);
                  }}
                />
              )}
            </StyledIconPlaceholder>
            <StyledReviewSectionTitle>
              <Text textStyle="body-bold">Square footage</Text>
              <TextButton
                aria-label={`learn-more-about-${ariaLabels.SQUARE_FOOTAGE}`}
                textStyle="body"
                onClick={() => {
                  setDisplayRecsModal(RECS.SQUARE_FOOTAGE);
                }}
              >
                Learn more
              </TextButton>
            </StyledReviewSectionTitle>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.SQUARE_FOOTAGE}
            value={MODAL_COMPONENT.SquareFootageFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={getSquareFootageDisplay()} />
      </Box>

      {isRoomForRent && (
        <Box css={reviewSectionStyles} data-testid="RoomSquareFootage">
          <StyledReviewTitleRow>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <StyledIconPlaceholder>
                {squareFootageRec && (
                  <RecommendationIconBulb
                    fieldName={ariaLabels.ROOM_SQUARE_FOOTAGE}
                    removeFlex={true}
                    onClick={() => {
                      setDisplayRecsModal(RECS.SQUARE_FOOTAGE);
                    }}
                  />
                )}
              </StyledIconPlaceholder>
              <Text textStyle="body-bold">Room square footage</Text>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.ROOM_SQUARE_FOOTAGE}
              value={MODAL_COMPONENT.RoomSquareFootageFormField.componentKey}
              onEditClick={onEditClick}
            />
          </StyledReviewTitleRow>
          <FieldValueDisplay value={getRoomSquareFootageDisplay()} />
        </Box>
      )}

      <Box css={reviewSectionStyles} data-testid="Beds">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {(floorplan?.beds ?? '') === '' && !isDisabled && (
                <AccordionIconError tabIndex={0} title="Bedrooms incomplete error" marginRight="xs" />
              )}
            </StyledIconPlaceholder>
            <Text textStyle="body-bold" asChild>
              <p>Bedrooms</p>
            </Text>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.BEDROOMS}
            value={MODAL_COMPONENT.BedroomsFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay
          value={String(floorplan?.beds) === '0' ? 'Studio' : String(floorplan?.beds || '')}
          customNoneText={customNoneFieldValueText}
        />
      </Box>

      <Box css={reviewSectionStyles} data-testid="Baths">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {String(floorplan?.baths || '') === '' && !isDisabled && (
                <AccordionIconError tabIndex={0} title="Bathrooms incomplete error" marginRight="xs" />
              )}
            </StyledIconPlaceholder>
            <Text textStyle="body-bold" asChild>
              <p>Bathrooms</p>
            </Text>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.BATHROOMS}
            value={MODAL_COMPONENT.BathroomsFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={String(floorplan?.baths || '')} customNoneText={customNoneFieldValueText} />
      </Box>

      <Box css={reviewSectionStyles} data-testid="PropertyType">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder />
            <Text textStyle="body-bold">Property type</Text>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.PROPERTY_TYPE}
            value={MODAL_COMPONENT.PropertyTypeFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={propertyType} />
      </Box>

      <Box data-testid="PropertyDescription">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            {isRoomForRent && !description ? (
              <StyledIconPlaceholder>
                <AccordionIconError tabIndex={0} title="Description incomplete error" marginRight="xs" />
              </StyledIconPlaceholder>
            ) : (
              <StyledIconPlaceholder>
                {descriptionRec && (
                  <RecommendationIconBulb
                    fieldName={ariaLabelForPropertyDescription}
                    removeFlex={true}
                    onClick={() => {
                      setDisplayRecsModal(RECS.DESCRIPTION);
                    }}
                  />
                )}
              </StyledIconPlaceholder>
            )}

            <StyledReviewSectionTitle>
              <Text textStyle="body-bold">{propertyDescriptionHeading}</Text>
              <TextButton
                aria-label={`learn-more-about-${ariaLabelForPropertyDescription}`}
                textStyle="body"
                onClick={() => {
                  setDisplayRecsModal(isRoomForRent ? RECS.ROOM_DESCRIPTION : RECS.DESCRIPTION);
                }}
              >
                Learn more
              </TextButton>
            </StyledReviewSectionTitle>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabelForPropertyDescription}
            value={MODAL_COMPONENT.PropertyDescriptionFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>
        <FieldValueDisplay value={description} />
      </Box>
      <CloneThisListingConversionModal />
      {displayRecsModal && (
        <RecommendationsLearnMoreContent
          onClose={() => setDisplayRecsModal(false)}
          recommendationType={displayRecsModal}
        />
      )}
    </div>
  );
};

PropertyInformationContent.propTypes = propTypes;
PropertyInformationContent.defaultProps = defaultProps;

export default PropertyInformationContent;
